import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  ErrorHandler,
  OnInit,
  inject
} from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  defer,
  distinctUntilChanged,
  EMPTY,
  filter,
  map,
  mergeMap,
  Observable,
  startWith
} from 'rxjs';
import { GlobalEventsService } from '@coin/shared/core/util-helpers';
import { CoinUser, UserMenuItem } from '@coin/shared/core/util-models';
import { SetUserImageService } from '@coin/shared/core/data-access';
import { TokenService, UserState } from '@coin/shared/auth/feature';
import { AuthService } from '@coin/shared/auth/data-access';
import { ConfirmationDialogComponent, V2ButtonComponent, V2CardComponent } from '@coin/shared/core/ui-storybook';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { EmulationService } from './services/emulation.service';
import { LanguageMenuService } from './services/language-menu.service';
import { UserMenuState } from './state/user-menu.state';
import { NgStyle, NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { TruncatedTooltipDirective } from '../../../../shared/directives/truncated-tooltip.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { NamePipe } from '../../../../shared/pipes/name.pipe';
import { PreventRightClickDirective } from '@coin/shared/core/util-directives';

@Component({
  selector: 'cosmos-user-menu',
  templateUrl: './user-menu.component.html',
  styleUrls: ['./user-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    V2ButtonComponent,
    PreventRightClickDirective,
    NgStyle,
    MatIcon,
    TruncatedTooltipDirective,
    V2CardComponent,
    NgTemplateOutlet,
    AsyncPipe,
    TranslatePipe,
    NamePipe
  ]
})
export class UserMenuComponent implements OnInit {
  private readonly userState = inject(UserState);
  private readonly userMenuState = inject(UserMenuState);
  private readonly elementRef = inject(ElementRef);
  private readonly eventsService = inject(GlobalEventsService);
  private readonly authService = inject(AuthService);
  private readonly setUserImageService = inject(SetUserImageService);
  private readonly languageMenuService = inject(LanguageMenuService);
  emulationService = inject(EmulationService);
  tokenService = inject(TokenService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly errorHandler = inject(ErrorHandler);

  public isMenuVisible$ = new BehaviorSubject<boolean>(false);

  public user$: Observable<CoinUser> = this.userState.user$;
  private readonly userImageSrcChange$ = toObservable(this.userMenuState.userImageSource);
  public userImageSrc$: Observable<string> = defer(() =>
    this.userImageSrcChange$.pipe(
      map(() => this.userMenuState.userImageSource()),
      startWith(this.userMenuState.userImageSource()),
      distinctUntilChanged()
    )
  );
  public userDetails = this.userMenuState.userDetails;

  public remainingTokenTimeText$: Observable<string> = this.tokenService.remainingTimeText$;
  public tokenTimeWarningStyle$: Observable<'ok' | 'alarming' | 'urgent'> = this.tokenService.timeWarningStyle$;

  public menuItems$: Observable<UserMenuItem[]>;

  constructor() {
    this.tokenService.init(ConfirmationDialogComponent);
  }

  public async ngOnInit(): Promise<void> {
    this.user$
      .pipe(
        filter(user => !!user),
        // eslint-disable-next-line rxjs/no-unsafe-takeuntil -- ngxs compatibility: started calls must finish like dispatched actions
        takeUntilDestroyed(this.destroyRef),
        mergeMap(user => {
          this.userMenuState.loadUserDetails(user);
          return this.userMenuState.loadUserImage(user).pipe(
            // ngxs compatibility: a failed dispatch was only reported, it did not end this stream
            catchError(error => {
              this.errorHandler.handleError(error);
              return EMPTY;
            })
          );
        })
      )
      .subscribe();

    await this.setupMenuItems();

    this.tokenService.startCheckingTimeTillLogout();

    this.eventsService
      .listen('click')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(e => this.clickoutHandler(e));
  }

  public toggleMenu(): void {
    this.isMenuVisible$.next(!this.isMenuVisible$.value);
  }

  public openMenuItem(item?: UserMenuItem): void {
    if (item?.subMenuItems?.length > 0) {
      item.submenuactive = !item.submenuactive;
    } else if (item?.action) {
      item.action();

      this.toggleMenu();
    }
  }

  public async renewToken(): Promise<void> {
    await this.tokenService.renewToken();
  }

  private clickoutHandler(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      if (this.isMenuVisible$.value) {
        this.toggleMenu();
      }
    }
  }

  private async setupMenuItems(): Promise<void> {
    // As long as user profile are not implemented the menu item will remain commented out
    this.menuItems$ = this.userImageSrc$.pipe(
      map(imgSrc => {
        const menuItems: UserMenuItem[] = [
          // { name: 'header.user-menu.menu-item-profile', icon: 'person', submenuactive: false },
          {
            name: !imgSrc ? 'header.user-menu.menu-item-add-picture' : 'header.user-menu.menu-item-modify-picture',
            icon: 'edit',
            action: () => this.setUserImageService.setPersonalPicture()
          },
          {
            name: `header.user-menu.languages.${this.languageMenuService.selectedLanguage}`,
            icon: 'language',
            subMenuItems: this.languageMenuService.getSelectableLanguageMenuItems()
          }
        ];

        if (this.emulationService.isEmulating) {
          menuItems.push({
            name: 'header.user-menu.menu-item-stop-emulate',
            icon: 'person_off',
            action: () => this.emulationService.removeEmulation()
          });
        } else if (this.emulationService.isEmulationPermitted()) {
          menuItems.push({
            name: 'header.user-menu.menu-item-emulate',
            icon: 'people',
            action: () => this.emulationService.selectEmulationUser()
          });
        }

        menuItems.push({
          name: 'header.user-menu.menu-item-log-out',
          icon: 'logout',
          action: () => this.authService.logOff()
        });

        return menuItems;
      })
    );
  }
}
